/**
 * Template Service for Representative Module
 * Handles API calls for NUC scoring templates and scores
 */

import { get, post, del } from "./apiClient";

/**
 * Get templates by programme mode
 * @param {string} programmeMode - Programme mode (e.g., 'Full-Time', 'Part-Time', 'Sandwich')
 * @param {string|null} type - Optional template type filter
 * @returns {Promise<Array>} List of templates
 */
export const getTemplatesByMode = async (programmeMode, type = null) => {
  try {
    console.log('[TemplateService] Fetching templates:', { programmeMode, type });

    const url = `/nuc_scoring/mode/${programmeMode}`;
    console.log('[TemplateService] Request URL:', url);

    const response = await get(url);
    console.log('[TemplateService] Response:', response);
    console.log('[TemplateService] Response type:', typeof response);
    console.log('[TemplateService] Is array?:', Array.isArray(response));

    // nuc_scoring returns array directly, not wrapped in success object
    if (Array.isArray(response)) {
      console.log('[TemplateService] Templates fetched:', response.length);
      // Filter by type if specified
      if (type) {
        const filtered = response.filter(template => template.type === type);
        console.log('[TemplateService] Filtered templates:', filtered.length);
        return filtered;
      }
      return response;
    }

    // If response is not an array, check if it's an error object
    if (response && response.type === 'error') {
      throw new Error(response.message || 'Failed to fetch templates');
    }

    // If we got here, response format is unexpected but not necessarily an error
    // Return empty array as fallback
    console.warn('[TemplateService] Unexpected response format, returning empty array');
    return [];
  } catch (error) {
    console.error('[TemplateService] Error fetching templates:', error);
    console.error('[TemplateService] Error details:', {
      message: error.message,
      response: error.response,
      status: error.response?.status
    });
    throw error;
  }
};

/**
 * Get scores for a specific accreditation
 * @param {string} accreditationId - Accreditation ID
 * @param {string|null} templateId - Optional template ID filter
 * @returns {Promise<Object>} Scores data
 */
export const getScoresForAccreditation = async (accreditationId, templateId = null) => {
  try {
    let url = `/nuc_scoring/scores/${accreditationId}`;

    if (templateId) {
      url += `?templateId=${templateId}`;
    }

    const response = await get(url);

    if (response.success || response.type === 'success') {
      return response.data || response.scores || {};
    }

    throw new Error(response.message || 'Failed to fetch scores');
  } catch (error) {
    console.error('Error fetching scores:', error);
    throw error;
  }
};

/**
 * Save template scores
 * @param {Object} scoreData - Score data object
 * @param {string} scoreData.representativeId - Representative ID
 * @param {string} scoreData.accreditationId - Accreditation ID
 * @param {string} scoreData.programmeId - Programme ID
 * @param {string} scoreData.templateId - Template ID
 * @param {Object} scoreData.itemScores - Object of item scores with itemId as key and {itemId, itemName, maxScore, enteredValue, isNotApplicable} as value
 * @param {string} scoreData.status - Status (draft or submitted)
 * @returns {Promise<Object>} Save response
 */
export const saveTemplateScores = async (scoreData) => {
  try {
    const { representativeId, accreditationId, programmeId, templateId, itemScores, status = 'draft' } = scoreData;

    if (!representativeId || !accreditationId || !programmeId || !templateId || !itemScores) {
      throw new Error('Missing required fields: representativeId, accreditationId, programmeId, templateId, or itemScores');
    }

    if (typeof itemScores !== 'object' || Object.keys(itemScores).length === 0) {
      throw new Error('itemScores must be a non-empty object');
    }

    const response = await post('/nuc_scoring/scores/save', {
      representativeId,
      accreditationId,
      programmeId,
      templateId,
      itemScores,
      status,
    });

    if (response.type === 'success' || response.success) {
      return response.data || {};
    }

    throw new Error(response.message || 'Failed to save scores');
  } catch (error) {
    console.error('Error saving scores:', error);
    throw error;
  }
};

/**
 * Get completion status for an accreditation
 * @param {string} accreditationId - Accreditation ID
 * @returns {Promise<Object>} Completion status data
 */
export const getCompletionStatus = async (accreditationId) => {
  try {
    const response = await get(`/nuc_scoring/scores/status/${accreditationId}`);

    if (response.type === 'success' || response.success) {
      return response.data || {};
    }

    throw new Error(response.message || 'Failed to fetch completion status');
  } catch (error) {
    console.error('Error fetching completion status:', error);
    throw error;
  }
};

/**
 * Submit scores for review
 * @param {string} accreditationId - Accreditation ID
 * @returns {Promise<Object>} Submit response
 */
export const submitScores = async (accreditationId) => {
  try {
    console.log('📤 Submitting scores for accreditation:', accreditationId);
    const response = await post('/nuc_scoring/scores/submit', {
      accreditationId,
    });

    console.log('✅ Submit response:', response);

    // Backend returns { type: 'success', message: '...', data: {...} }
    if (response.type === 'success') {
      return response.data || {};
    }

    throw new Error(response.message || 'Failed to submit scores');
  } catch (error) {
    console.error('❌ Error submitting scores:', error);
    throw error;
  }
};

/**
 * Get template by ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} Template data
 */
export const getTemplateById = async (templateId) => {
  try {
    const response = await get(`/nuc_scoring/${templateId}`);

    if (response.success) {
      return response.data || null;
    }

    throw new Error(response.message || 'Failed to fetch template');
  } catch (error) {
    console.error('Error fetching template:', error);
    throw error;
  }
};

/**
 * Save deficiencies and remedies for an accreditation
 */
export const saveDeficienciesAndRemedies = async (accreditationId, deficiencies, remedies) => {
  try {
    const response = await post(
      `/nuc_accreditation/deficiencies/${accreditationId}`,
      { deficiencies, remedies }
    );

    if (response.type === 'success') {
      return response.data;
    }

    throw new Error(response.message || 'Failed to save deficiencies and remedies');
  } catch (error) {
    console.error('Error saving deficiencies and remedies:', error);
    throw error;
  }
};

/**
 * Get deficiencies and remedies for an accreditation
 */
export const getDeficienciesAndRemedies = async (accreditationId) => {
  try {
    const response = await get(
      `/nuc_accreditation/deficiencies/${accreditationId}`
    );

    if (response.type === 'success') {
      return response.data;
    }

    throw new Error(response.message || 'Failed to fetch deficiencies and remedies');
  } catch (error) {
    console.error('Error fetching deficiencies and remedies:', error);
    throw error;
  }
};

/**
 * Get submission summary for Results page
 * Fetches completion status and calculates totals
 */
export const getSubmissionSummary = async (accreditationId) => {
  try {
    // Fetch completion status (includes all template scores)
    const statusData = await getCompletionStatus(accreditationId);

    console.log('[getSubmissionSummary] statusData:', statusData);

    // Calculate total score from all templates
    let totalScore = 0;
    let maxPossibleScore = 0;

    // Use templateStatus from the API response (not templates)
    const templateStatus = statusData?.templateStatus || [];

    templateStatus.forEach(template => {
      if (template.totalScore) {
        totalScore += template.totalScore;
      }
      if (template.maxPossibleScore) {
        maxPossibleScore += template.maxPossibleScore;
      }
    });

    console.log('[getSubmissionSummary] Calculated:', { totalScore, maxPossibleScore });

    // Determine recommendation based on percentage
    const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;
    let recommendation = "";
    let recommendationReason = "";

    if (percentage >= 90) {
      recommendation = "FULL ACCREDITATION";
      recommendationReason = `The programme scored ${percentage.toFixed(1)}% (${totalScore.toFixed(2)}/${maxPossibleScore}), which meets the threshold for full accreditation (≥90%).`;
    } else if (percentage >= 70) {
      recommendation = "INTERIM ACCREDITATION";
      recommendationReason = `The programme scored ${percentage.toFixed(1)}% (${totalScore.toFixed(2)}/${maxPossibleScore}), which qualifies for interim accreditation (70-89%).`;
    } else {
      recommendation = "NOT ACCREDITED";
      recommendationReason = `The programme scored ${percentage.toFixed(1)}% (${totalScore.toFixed(2)}/${maxPossibleScore}), which is below the minimum threshold for accreditation (<70%).`;
    }

    // Transform templateStatus to match expected format for Results page
    const templates = templateStatus.map(template => ({
      _id: template.templateId,
      templateId: template.templateId,
      name: template.templateName,
      templateName: template.templateName,
      type: template.templateType,
      totalScore: template.totalScore || 0,
      maxPossibleScore: template.maxPossibleScore || 0,
      status: template.status,
      completionStatus: template.completionStatus,
    }));

    console.log('[getSubmissionSummary] Returning:', {
      totalScore,
      maxPossibleScore,
      percentage,
      recommendation,
      hasSubmitted: statusData?.overallStatus?.isSubmitted,
      templatesCount: templates.length,
    });

    return {
      totalScore,
      maxPossibleScore,
      percentage,
      recommendation,
      recommendationReason,
      templates,
      overallStatus: statusData?.overallStatus || {},
      hasSubmitted: statusData?.overallStatus?.isSubmitted === true,
      lastModified: statusData?.lastModified || null,
    };
  } catch (error) {
    console.error('Error getting submission summary:', error);
    throw error;
  }
};

/**
 * Get accreditation documents (self-study form and evaluation form)
 * @param {string} accreditationId - Accreditation ID
 * @returns {Promise<Object>} Documents data
 */
export const getAccreditationDocuments = async (accreditationId) => {
  try {
    const response = await get(`/nuc_accreditation/documents/${accreditationId}`);

    if (response.type === 'success') {
      return response.data;
    }

    throw new Error(response.message || 'Failed to fetch accreditation documents');
  } catch (error) {
    console.error('Error fetching accreditation documents:', error);
    throw error;
  }
};

/**
 * Upload representative document
 * @param {string} accreditationId - Accreditation ID
 * @param {FormData} formData - Form data containing title and file
 * @returns {Promise<Object>} Upload response
 */
export const uploadRepresentativeDocument = async (accreditationId, formData) => {
  try {
    const response = await post(`/nuc_accreditation/representative-documents/${accreditationId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.type === 'success') {
      return response.data;
    }

    throw new Error(response.message || 'Failed to upload document');
  } catch (error) {
    console.error('Error uploading representative document:', error);
    throw error;
  }
};

/**
 * Get representative documents
 * @param {string} accreditationId - Accreditation ID
 * @returns {Promise<Object>} Documents data
 */
export const getRepresentativeDocuments = async (accreditationId) => {
  try {
    const response = await get(`/nuc_accreditation/representative-documents/${accreditationId}`);

    if (response.type === 'success') {
      return response.data;
    }

    throw new Error(response.message || 'Failed to fetch representative documents');
  } catch (error) {
    console.error('Error fetching representative documents:', error);
    throw error;
  }
};

/**
 * Delete representative document
 * @param {string} accreditationId - Accreditation ID
 * @param {string} documentId - Document ID
 * @returns {Promise<Object>} Delete response
 */
export const deleteRepresentativeDocument = async (accreditationId, documentId) => {
  try {
    const response = await del(`/nuc_accreditation/representative-documents/${accreditationId}/${documentId}`);

    if (response.type === 'success') {
      return response;
    }

    throw new Error(response.message || 'Failed to delete document');
  } catch (error) {
    console.error('Error deleting representative document:', error);
    throw error;
  }
};

export default {
  getTemplatesByMode,
  getScoresForAccreditation,
  saveTemplateScores,
  getCompletionStatus,
  saveDeficienciesAndRemedies,
  getDeficienciesAndRemedies,
  submitScores,
  getTemplateById,
  getSubmissionSummary,
  getAccreditationDocuments,
  uploadRepresentativeDocument,
  getRepresentativeDocuments,
  deleteRepresentativeDocument,
};
